import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useToast } from '@orbind/ui'
import { escutarDoDesktop } from '../app/desktop-eventos.ts'
import { notificarNoSistema } from '../chat/notificacao-nativa.ts'
import { canalDePresenca } from '../presence/presence-client.ts'

/**
 * Avisos de NOTIFICAÇÃO (SPEC-20260924-2111) — montado no shell, vale em
 * qualquer tela.
 *
 * Duas saídas, escolhidas pelo foco da janela, como o usuário pediu:
 *
 * - **Orbind na frente:** o cartão DENTRO do app, com "Abrir" — quem está
 *   olhando a tela não precisa de notificação do sistema, e as duas juntas são
 *   barulho.
 * - **Orbind na bandeja ou atrás:** a notificação do SISTEMA, no mesmo molde do
 *   aviso do chat (toast nativo do Windows com nome e ícone do Orbind), com o
 *   botão "Abrir o item". O `notificarNoSistema` desiste sozinho quando a
 *   janela está em foco, então as duas nunca saem juntas.
 *
 * Com o app INTEIRAMENTE fechado não há cliente conectado e nada aparece na
 * hora — a notificação fica no banco e está lá quando a pessoa abrir. É o mesmo
 * limite do aviso do chat.
 *
 * Não filtra por status declarado, diferente do chat: atribuição é trabalho
 * dirigido a UMA pessoa, e ela precisa saber mesmo que esteja ausente.
 */
export function AvisosDeNotificacao() {
  const navigate = useNavigate()
  const toast = useToast()

  // refs: o efeito assina o canal UMA vez, e uma dependência que muda a cada
  // render reassinaria a cada render
  const abrir = useRef(navigate)
  abrir.current = navigate
  const mostrar = useRef(toast.push)
  mostrar.current = toast.push

  useEffect(() => {
    return canalDePresenca.inscrever((evento) => {
      if (evento.tipo !== 'notificacao') return

      // convite para evento do Calendário (SPEC-20261027-2350-calendario-real)
      const convite = evento.kind === 'calendar_invite'
      const onde = evento.code ? `${evento.code} · ${evento.titulo}` : evento.titulo
      const frase = convite
        ? `${evento.autor} convidou você para "${evento.titulo}".`
        : `${evento.autor} atribuiu ${onde} a você.`
      const irParaOItem = () => {
        if (convite && evento.calendarEventId) {
          const dia = evento.data ? `data=${evento.data}&` : ''
          abrir.current(`/calendar?${dia}evento=${evento.calendarEventId}`)
        } else if (evento.workItemId) abrir.current(`/work-items/${evento.workItemId}`)
        else abrir.current('/notifications')
      }

      if (document.hasFocus()) {
        mostrar.current({ tone: 'info', message: frase, action: { label: 'Abrir', onClick: irParaOItem } })
        return
      }

      notificarNoSistema({
        titulo: convite ? 'Convite para um evento' : 'Novo Item de Trabalho para você',
        corpo: frase,
        // por ITEM: dois avisos do mesmo item substituem, não empilham
        grupo: `notificacao:${evento.workItemId ?? evento.id}`,
        // no navegador o clique volta por aqui; no desktop, pelo evento abaixo
        aoClicar: irParaOItem,
        ...(evento.workItemId ? { item: { workItemId: evento.workItemId } } : {}),
      })
    })
  }, [])

  /**
   * Clique no toast do Windows (shell 0.3.5+): o Rust traz a janela e manda o
   * id do item por aqui. Shell antigo sem o comando cai na notificação simples,
   * que não devolve clique — e aí o sino continua sendo o caminho.
   */
  useEffect(() => {
    return escutarDoDesktop<{ workItemId: string }>('orbind://notificacao-abrir', (r) => {
      if (r.workItemId) abrir.current(`/work-items/${r.workItemId}`)
    })
  }, [])

  /**
   * Link `orbind://…` aberto pelo sistema (shell 0.3.7+, SPEC-20261103-2105):
   * o Rust traduz em caminho do app e manda por aqui. Só caminhos internos —
   * um link malicioso não leva a lugar nenhum fora do Orbind.
   */
  useEffect(() => {
    return escutarDoDesktop<{ path: string }>('orbind://abrir-url', (r) => {
      if (typeof r.path === 'string' && r.path.startsWith('/') && !r.path.startsWith('//')) abrir.current(r.path)
    })
  }, [])

  return null
}
